import { Button, Modal } from "@gadgetinc/widgets";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { observer } from "mobx-react-lite";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { PaymentMethodProps } from "state-trees/src/Billing";
import { useBillingFetch, useCreateSetupIntent } from "../Billing";
import { AddPaymentMethodForm, useAddPaymentForm } from "./AddPaymentMethodForm";
import { useBillingSession } from "./BillingContext";
import { StripeContext, StripeElementsProvider, useConfirmSetupIntent } from "./Stripe";

const DefaultError = "There was an issue adding your card, please try again.";

const AddPaymentModalBody = observer(
  (props: {
    loadingSetupIntent: boolean;
    setupIntentSecret: string | undefined;
    onSuccess: (paymentMethod: PaymentMethodProps) => void | Promise<void>;
  }) => {
    const { loadingSetupIntent, setupIntentSecret, onSuccess } = props;
    const stripe = useStripe();
    const elements = useElements();

    const billingSession = useBillingSession();

    const { formComplete, setShowFormIncomplete, form } = useAddPaymentForm();

    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);

    const setDefaultPaymentMethod = useBillingFetch<PaymentMethodProps | { message: string }>(
      "/auth/api/billing/default-payment-method"
    ).post;

    const confirmSetupIntent = useConfirmSetupIntent(async (paymentMethodId) => {
      const defaultMethodResult = (await setDefaultPaymentMethod({ paymentMethodId })) as
        | { paymentMethod: PaymentMethodProps }
        | { message: string };

      return "paymentMethod" in defaultMethodResult
        ? { success: true, result: { paymentMethod: defaultMethodResult.paymentMethod } }
        : { success: false, message: defaultMethodResult.message };
    });

    const addCard = useCallback(async () => {
      setErrors([]);
      setShowFormIncomplete(false);

      if (!stripe || !elements || !setupIntentSecret) {
        setErrors([DefaultError]);
        return;
      }

      if (!formComplete) {
        setShowFormIncomplete(true);
        return;
      }

      try {
        const result = await confirmSetupIntent(stripe, elements, setupIntentSecret, () => setSubmitting(true));

        if (result.success) {
          await onSuccess(result.paymentMethod);
        } else {
          setErrors([result.message || DefaultError]);
        }
      } catch {
        setErrors([DefaultError]);
      }

      setSubmitting(false);
    }, [stripe, elements, setupIntentSecret, formComplete, setShowFormIncomplete, confirmSetupIntent, onSuccess]);

    const button = (
      <Button kind="primary" onClick={addCard} disabled={submitting} isLoading={submitting}>
        Add Card
      </Button>
    );

    return (
      <AddPaymentMethodForm
        loading={loadingSetupIntent}
        form={form}
        button={button}
        errors={errors}
        defaultCountry={billingSession.paymentMethod?.customerBillingCountry ?? undefined}
        minHeight="388px"
      />
    );
  }
);

export const AddPaymentModal = observer((props: { isOpen?: boolean; closeModal: () => void }) => {
  const { isOpen, closeModal } = props;
  const stripe = useContext(StripeContext);
  const { createSetupIntent, loadingSetupIntent, setupIntentData } = useCreateSetupIntent();

  useEffect(() => {
    if (isOpen) {
      void createSetupIntent({}).catch((error: any) => {
        console.error(error);
      });
    }
  }, [isOpen, createSetupIntent]);

  const setupIntentSecret = useMemo(() => {
    return setupIntentData?.clientSecret;
  }, [setupIntentData]);

  const billingSession = useBillingSession();

  const onClose = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const onSuccess = useCallback(
    (paymentMethod: PaymentMethodProps) => {
      billingSession.setPaymentMethod(paymentMethod);
      onClose();
    },
    [onClose, billingSession]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      overrides={{
        Dialog: {
          style: {
            width: "518px",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
      title="Payment information"
    >
      <StripeElementsProvider stripe={stripe}>
        <AddPaymentModalBody loadingSetupIntent={loadingSetupIntent} setupIntentSecret={setupIntentSecret} onSuccess={onSuccess} />
      </StripeElementsProvider>
    </Modal>
  );
});
